import { usePreviewWithProgress } from '@/hooks/usePreviewWithProgress';
import { FoldersPageTableData } from '@/pages/FoldersPages/FoldersPage/useFoldersPageColumns';
import { downloadStore } from '@/stores/downloadStore';
import {
  chip,
  cn,
  Progress,
  Slider,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@skand/ui';
import { Row } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';

interface ImagePreviewWithProgressProps {
  row: Row<FoldersPageTableData>;
  url: string;
  close: () => void;
  downloadable?: boolean;
  zoomable?: boolean;
  imageFileName?: string;
}

const minZoom = 0;
const maxZoom = 90;
const zoomStep = 5;

export const ImagePreviewWithProgress = ({
  row,
  url,
  close: handleClose,
  downloadable = false,
  zoomable = false,
  imageFileName,
}: ImagePreviewWithProgressProps): JSX.Element => {
  const [zoom, setZoom] = useState(minZoom);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [fadeOut, setFadeOut] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  const { progress, loading } = usePreviewWithProgress(url);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(value, max));

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || zoom === 0 || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const imgElement = containerRef.current.querySelector('img');
    if (!imgElement) return;

    const zoomScale = 1 + zoom * 0.1;
    const scaledWidth = imgElement.naturalWidth * zoomScale;
    const scaledHeight = imgElement.naturalHeight * zoomScale;

    const maxOffsetX = Math.max(0, (scaledWidth - containerRect.width) / 2);
    const maxOffsetY = Math.max(0, (scaledHeight - containerRect.height) / 2);

    const newX = e.clientX - startPos.x;
    const newY = e.clientY - startPos.y;

    setPosition({
      x: clamp(newX, -maxOffsetX, maxOffsetX),
      y: clamp(newY, -maxOffsetY, maxOffsetY),
    });
  };

  // Handle zoom setting
  const handleZoom = (zoom: number) => {
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));
    setZoom(clampedZoom);
  };

  // Handle downloading the photo
  const handleDownload = () => downloadStore.createTaskManagerAndStart([row.id]);

  useEffect(() => {
    if (loading) {
      setShowOverlay(true);
      setFadeOut(false);
    } else {
      setFadeOut(true);
      setTimeout(() => setShowOverlay(false), 300);
    }
  }, [loading]);

  useEffect(() => {
    const handleWindowMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => window.removeEventListener('mouseup', handleWindowMouseUp);
  }, []);

  useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, [zoom]);

  return (
    <div className={cn(
      'fixed',
      'inset-0',
      'z-800',
      'flex',
      'items-center',
      'justify-center',
      'bg-neutral-800/80'
    )}>
      <div
        className={cn(
          'relative w-[900px] h-[600px] rounded-lg bg-neutral-100 overflow-show shadow-[0_4px_10px_rgba(0,0,0,0.3)] flex items-center justify-center',
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        )}
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
      >
        {
          !showOverlay && (
            <div
              className={cn(
                'relative',
                'h-full w-full',
                'flex items-center justify-center',
                'overflow-hidden'
              )}
            >
              <img
                alt={imageFileName}
                className={cn(
                  'absolute max-w-full max-h-full object-contain select-none pointer-events-none will-change-transform',
                  !isDragging && 'transition-transform duration-100 ease-in-out',
                )}
                draggable={false}
                src={url}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${1 + zoom * 0.1})`,
                }}
              />
            </div>
          )
        }

        <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-2">
          <div className="pointer-events-auto flex items-center justify-between">
            <div
              className={cn(
                !zoomable && 'hidden',
                'flex',
                'flex-row',
                'gap-1',
                'flex',
                'flex-row',
                'items-center',
                'justify-between',
                'mr-6',
              )}
            >
              <div
                className={cn(
                  'h-5',
                  'w-5',
                  'flex',
                  'items-center',
                  'justify-center',
                  'border-1',
                  'rounded-1',
                  'border-solid',
                  'bg-neutral-100',
                  loading ? 'color-neutral-300' : 'color-neutral-600 border-neutral-500 hover:color-neutral-100 hover:bg-primary-400 hover:border-0',
                )}
                onClick={loading ? undefined : () => handleZoom(zoom - zoomStep)}
              >
                <div className={cn('i-skand-subtract', 'cursor-pointer', 'text-12px')} />
              </div>
              <div className={cn('w-163px', !loading && 'cursor-pointer')}>
                <Slider
                  disabled={loading}
                  max={maxZoom}
                  min={minZoom}
                  step={zoomStep}
                  value={[zoom]}
                  onValueChange={value => handleZoom(value[0])}
                />
              </div>
              <div
                className={cn(
                  'h-5',
                  'w-5',
                  'flex',
                  'items-center',
                  'justify-center',
                  'border-1',
                  'rounded-1',
                  'border-solid',
                  'bg-neutral-100',
                  loading ? 'color-neutral-300' : 'color-neutral-600 border-neutral-500 hover:color-neutral-100  hover:bg-primary-400 hover:border-0',
                )}
                onClick={loading ? undefined : () => handleZoom(zoom + zoomStep)}
              >
                <div className={cn('i-skand-add', 'cursor-pointer', 'text-12px')} />
              </div>
            </div>

            <div className={cn('flex', 'gap-3')}>
              <TooltipProvider className={cn(!downloadable && 'hidden')}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        'cursor-pointer',
                        'bg-transparent',
                        'b-none',
                        loading ? 'color-neutral-300' : 'color-neutral-500',
                      )}
                      disabled={loading}
                      onClick={handleDownload}
                    >
                      <div
                        className={cn(
                          'text-15px',
                          'text-right',
                          'i-skand-download',
                          !loading && 'hover:text-primary-400',
                        )}
                      />
                    </button>
                  </TooltipTrigger>

                  <TooltipContent className={chip({
                    size: 'medium',
                  })}>
                    <p className={cn(
                      'w-auto',
                      'bg-neutral-800',
                      'text-neutral-100',
                      'typo-text-small',
                      'px-2',
                      'py-1',
                      'rounded-1',
                      'gap-10px',
                      'whitespace-nowrap',
                    )}>Download Image</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        'cursor-pointer',
                        'bg-transparent',
                        'b-none',
                        'color-neutral-500',
                      )}
                      onClick={handleClose}
                    >
                      <div
                        className={cn(
                          'text-12px',
                          'text-right',
                          'i-skand-close',
                          'hover:text-primary-400',
                        )}
                      />
                    </button>
                  </TooltipTrigger>

                  <TooltipContent className={chip({
                    size: 'medium',
                  })}>
                    <p className={cn(
                      'w-auto',
                      'bg-neutral-800',
                      'text-neutral-100',
                      'typo-text-small',
                      'px-2',
                      'py-1',
                      'rounded-1',
                      'gap-10px',
                      'whitespace-nowrap',
                    )}>Close</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>

        {showOverlay && (
          <div
            className={cn(
              'pointer-events-auto bg-neutral-100/60 transition-opacity duration-300',
              fadeOut ? 'opacity-0' : 'opacity-100'
            )}
          >
            <div className="h-[10px] w-[120px]">
              <Progress progress={progress / 100 || 0.01} />
            </div>
          </div>
        )}
      </div>
    </div >
  );
};
